<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1200"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="closing-settlement-modal" data-tour="bb-test-settlement-dialog">
      <div class="csm-header">
        <div class="csm-header-icon">
          <v-icon size="22" color="white">mdi-table-large</v-icon>
        </div>
        <div class="csm-header-text">
          <h3>결산</h3>
          <p>
            {{ year }}년 {{ periodHint }}
            <template v-if="bizGubun"> · {{ bizGubun }}</template>
            · 예산 / 결산 / 증감
          </p>
        </div>
        <button type="button" class="csm-close" @click="emit('update:modelValue', false)">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="csm-toolbar">
        <label class="csm-filter-label">구분</label>
        <select v-model="mainGubun" class="csm-filter-select">
          <option value="전체">전체</option>
          <option value="세입">세입</option>
          <option value="세출">세출</option>
        </select>
        <span v-if="payload?.periodLabel" class="csm-period">{{ payload.periodLabel }}</span>
      </div>

      <div class="csm-body">
        <div v-if="loading" class="csm-state">
          <v-progress-circular indeterminate size="28" width="2" color="#1e3a5f" />
          <span>결산 불러오는 중...</span>
        </div>
        <div v-else-if="error" class="csm-state is-error">
          <p>{{ error }}</p>
          <button type="button" class="csm-retry" @click="load">다시 시도</button>
        </div>
        <div v-else class="csm-table-wrap">
          <table class="csm-table">
            <thead>
              <tr>
                <th class="col-gubun">구분</th>
                <th class="col-gwan">관</th>
                <th class="col-hang">항</th>
                <th class="col-mok">목</th>
                <th class="col-source">구분</th>
                <th class="col-amt">정부보조금</th>
                <th class="col-amt">시설보조</th>
                <th class="col-amt">후원금</th>
                <th class="col-amt">계</th>
              </tr>
            </thead>
            <tbody>
              <!-- 세입 -->
              <template v-if="mainGubun === '전체' || mainGubun === '세입'">
                <template v-if="!groupedIn.length">
                  <tr>
                    <td colspan="9" class="empty-cell">세입 계정과목이 없습니다</td>
                  </tr>
                </template>
                <template v-else>
                  <template v-for="(gwan, gwanIdx) in groupedIn" :key="'in-g-' + gwanIdx">
                    <template v-for="(hang, hangIdx) in gwan.hangGroups" :key="'in-h-' + gwanIdx + '-' + hangIdx">
                      <template v-for="(mok, mokIdx) in hang.mokGroups" :key="'in-m-' + gwanIdx + '-' + hangIdx + '-' + mokIdx">
                        <tr
                          v-for="(row, rowIdx) in mok.rows"
                          :key="'in-r-' + gwanIdx + '-' + hangIdx + '-' + mokIdx + '-' + rowIdx"
                          :class="{ 'diff-row': row.source === '증감' }"
                        >
                          <td
                            v-if="gwanIdx === 0 && hangIdx === 0 && mokIdx === 0 && rowIdx === 0"
                            :rowspan="inGubunRows"
                            class="cell-gubun"
                          >세입</td>
                          <td
                            v-if="hangIdx === 0 && mokIdx === 0 && rowIdx === 0"
                            :rowspan="gwan.totalRows"
                            class="cell-gwan"
                          >
                            {{ gwan.gwan }}<br /><span class="code">{{ gwan.lcode }}</span>
                          </td>
                          <td
                            v-if="mokIdx === 0 && rowIdx === 0"
                            :rowspan="hang.totalRows"
                            class="cell-hang"
                          >
                            {{ hang.hang }}<br /><span class="code">{{ hang.mcode }}</span>
                          </td>
                          <td v-if="rowIdx === 0" :rowspan="3" class="cell-mok">{{ mok.mok }}</td>
                          <td class="cell-source" :class="sourceClass(row.source)">{{ row.source }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.정부보조금) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.시설보조) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.후원금) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.총액) }}</td>
                        </tr>
                      </template>
                      <template v-if="hang.showSubtotal">
                        <tr
                          v-for="(sub, subIdx) in hang.subtotals"
                          :key="'in-hs-' + gwanIdx + '-' + hangIdx + '-' + subIdx"
                          class="subtotal-row hang-sub"
                          :class="{ 'diff-row': sub.source === '증감' }"
                        >
                          <td v-if="subIdx === 0" :rowspan="3" class="sub-label">소계</td>
                          <td class="cell-source" :class="sourceClass(sub.source)">{{ sub.source }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.정부보조금) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.시설보조) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.후원금) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.총액) }}</td>
                        </tr>
                      </template>
                    </template>
                    <template v-if="gwan.showSubtotal">
                      <tr
                        v-for="(sub, subIdx) in gwan.subtotals"
                        :key="'in-gs-' + gwanIdx + '-' + subIdx"
                        class="subtotal-row gwan-sub"
                        :class="{ 'diff-row': sub.source === '증감' }"
                      >
                        <td v-if="subIdx === 0" :rowspan="3" colspan="2" class="sub-label">소계</td>
                        <td class="cell-source" :class="sourceClass(sub.source)">{{ sub.source }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.정부보조금) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.시설보조) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.후원금) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.총액) }}</td>
                      </tr>
                    </template>
                  </template>
                  <tr class="total-row">
                    <td class="total-label" colspan="4" rowspan="3">세입 합계</td>
                    <td class="cell-source source-budget">예산</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.예산.정부보조금) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.예산.시설보조) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.예산.후원금) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.예산.총액) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td class="cell-source source-closing">결산</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.결산.정부보조금) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.결산.시설보조) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.결산.후원금) }}</td>
                    <td class="cell-amt">{{ formatAmt(inTotals.결산.총액) }}</td>
                  </tr>
                  <tr class="total-row diff-row">
                    <td class="cell-source source-diff">증감</td>
                    <td class="cell-amt" :class="negClass(inTotals.증감)">{{ formatAmt(inTotals.증감.정부보조금) }}</td>
                    <td class="cell-amt" :class="negClass(inTotals.증감)">{{ formatAmt(inTotals.증감.시설보조) }}</td>
                    <td class="cell-amt" :class="negClass(inTotals.증감)">{{ formatAmt(inTotals.증감.후원금) }}</td>
                    <td class="cell-amt" :class="negClass(inTotals.증감)">{{ formatAmt(inTotals.증감.총액) }}</td>
                  </tr>
                </template>
              </template>

              <!-- 세출 -->
              <template v-if="mainGubun === '전체' || mainGubun === '세출'">
                <template v-if="!groupedOut.length">
                  <tr>
                    <td colspan="9" class="empty-cell">세출 계정과목이 없습니다</td>
                  </tr>
                </template>
                <template v-else>
                  <template v-for="(gwan, gwanIdx) in groupedOut" :key="'out-g-' + gwanIdx">
                    <template v-for="(hang, hangIdx) in gwan.hangGroups" :key="'out-h-' + gwanIdx + '-' + hangIdx">
                      <template v-for="(mok, mokIdx) in hang.mokGroups" :key="'out-m-' + gwanIdx + '-' + hangIdx + '-' + mokIdx">
                        <tr
                          v-for="(row, rowIdx) in mok.rows"
                          :key="'out-r-' + gwanIdx + '-' + hangIdx + '-' + mokIdx + '-' + rowIdx"
                          :class="{ 'diff-row': row.source === '증감' }"
                        >
                          <td
                            v-if="gwanIdx === 0 && hangIdx === 0 && mokIdx === 0 && rowIdx === 0"
                            :rowspan="outGubunRows"
                            class="cell-gubun"
                          >세출</td>
                          <td
                            v-if="hangIdx === 0 && mokIdx === 0 && rowIdx === 0"
                            :rowspan="gwan.totalRows"
                            class="cell-gwan"
                          >
                            {{ gwan.gwan }}<br /><span class="code">{{ gwan.lcode }}</span>
                          </td>
                          <td
                            v-if="mokIdx === 0 && rowIdx === 0"
                            :rowspan="hang.totalRows"
                            class="cell-hang"
                          >
                            {{ hang.hang }}<br /><span class="code">{{ hang.mcode }}</span>
                          </td>
                          <td v-if="rowIdx === 0" :rowspan="3" class="cell-mok">{{ mok.mok }}</td>
                          <td class="cell-source" :class="sourceClass(row.source)">{{ row.source }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.정부보조금) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.시설보조) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.후원금) }}</td>
                          <td class="cell-amt" :class="negClass(row)">{{ formatAmt(row.총액) }}</td>
                        </tr>
                      </template>
                      <template v-if="hang.showSubtotal">
                        <tr
                          v-for="(sub, subIdx) in hang.subtotals"
                          :key="'out-hs-' + gwanIdx + '-' + hangIdx + '-' + subIdx"
                          class="subtotal-row hang-sub"
                          :class="{ 'diff-row': sub.source === '증감' }"
                        >
                          <td v-if="subIdx === 0" :rowspan="3" class="sub-label">소계</td>
                          <td class="cell-source" :class="sourceClass(sub.source)">{{ sub.source }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.정부보조금) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.시설보조) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.후원금) }}</td>
                          <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.총액) }}</td>
                        </tr>
                      </template>
                    </template>
                    <template v-if="gwan.showSubtotal">
                      <tr
                        v-for="(sub, subIdx) in gwan.subtotals"
                        :key="'out-gs-' + gwanIdx + '-' + subIdx"
                        class="subtotal-row gwan-sub"
                        :class="{ 'diff-row': sub.source === '증감' }"
                      >
                        <td v-if="subIdx === 0" :rowspan="3" colspan="2" class="sub-label">소계</td>
                        <td class="cell-source" :class="sourceClass(sub.source)">{{ sub.source }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.정부보조금) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.시설보조) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.후원금) }}</td>
                        <td class="cell-amt" :class="negClass(sub)">{{ formatAmt(sub.총액) }}</td>
                      </tr>
                    </template>
                  </template>
                  <tr class="total-row">
                    <td class="total-label" colspan="4" rowspan="3">세출 합계</td>
                    <td class="cell-source source-budget">예산</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.예산.정부보조금) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.예산.시설보조) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.예산.후원금) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.예산.총액) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td class="cell-source source-closing">결산</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.결산.정부보조금) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.결산.시설보조) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.결산.후원금) }}</td>
                    <td class="cell-amt">{{ formatAmt(outTotals.결산.총액) }}</td>
                  </tr>
                  <tr class="total-row diff-row">
                    <td class="cell-source source-diff">증감</td>
                    <td class="cell-amt" :class="negClass(outTotals.증감)">{{ formatAmt(outTotals.증감.정부보조금) }}</td>
                    <td class="cell-amt" :class="negClass(outTotals.증감)">{{ formatAmt(outTotals.증감.시설보조) }}</td>
                    <td class="cell-amt" :class="negClass(outTotals.증감)">{{ formatAmt(outTotals.증감.후원금) }}</td>
                    <td class="cell-amt" :class="negClass(outTotals.증감)">{{ formatAmt(outTotals.증감.총액) }}</td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

interface AmtRow {
  source: string;
  정부보조금: number;
  시설보조: number;
  후원금: number;
  총액: number;
}

interface FlatRow extends AmtRow {
  gubun: string;
  관: string;
  lcode: string;
  항: string;
  mcode: string;
  목: string;
  scode: string;
}

const props = defineProps<{
  modelValue: boolean;
  memberCode: string;
  year: string | number;
  month: string | number;
  bizGubun?: string;
  bankCode?: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const loading = ref(false);
const error = ref('');
const payload = ref<any>(null);
const mainGubun = ref<'전체' | '세입' | '세출'>('전체');

const periodHint = computed(() =>
  payload.value?.cumulativeLabel
  || (Number(props.month) <= 1 ? '1월' : `1~${Number(props.month)}월`)
);

const emptyTotals = () => ({
  예산: { 정부보조금: 0, 시설보조: 0, 후원금: 0, 총액: 0 },
  결산: { 정부보조금: 0, 시설보조: 0, 후원금: 0, 총액: 0 },
  증감: { 정부보조금: 0, 시설보조: 0, 후원금: 0, 총액: 0 },
});

const parseAmt = (v: unknown) => Number(v) || 0;

const calcSubtotals = (items: FlatRow[]): AmtRow[] =>
  (['예산', '결산', '증감'] as const).map((source) => {
    const list = items.filter((i) => i.source === source);
    return {
      source,
      정부보조금: list.reduce((s, i) => s + parseAmt(i.정부보조금), 0),
      시설보조: list.reduce((s, i) => s + parseAmt(i.시설보조), 0),
      후원금: list.reduce((s, i) => s + parseAmt(i.후원금), 0),
      총액: list.reduce((s, i) => s + parseAmt(i.총액), 0),
    };
  });

const groupData = (dataList: FlatRow[]) => {
  if (!dataList.length) return [];
  const gwanGroups: any[] = [];
  let currentGwan: any = null;
  let currentHang: any = null;
  let currentMok: any = null;

  for (const item of dataList) {
    if (!currentGwan || currentGwan.gwan !== item.관 || currentGwan.gubun !== item.gubun) {
      currentGwan = {
        gubun: item.gubun,
        gwan: item.관,
        lcode: item.lcode,
        hangGroups: [],
        subtotals: [] as AmtRow[],
        showSubtotal: false,
        totalRows: 0,
      };
      gwanGroups.push(currentGwan);
      currentHang = null;
      currentMok = null;
    }
    if (!currentHang || currentHang.hang !== item.항) {
      currentHang = {
        hang: item.항,
        mcode: item.mcode,
        mokGroups: [],
        subtotals: [] as AmtRow[],
        showSubtotal: false,
        totalRows: 0,
      };
      currentGwan.hangGroups.push(currentHang);
      currentMok = null;
    }
    if (!currentMok || currentMok.mok !== item.목) {
      currentMok = { mok: item.목, scode: item.scode, rows: [] as FlatRow[] };
      currentHang.mokGroups.push(currentMok);
    }
    currentMok.rows.push(item);
  }

  for (const gwan of gwanGroups) {
    for (const hang of gwan.hangGroups) {
      const allHangRows = hang.mokGroups.flatMap((m: any) => m.rows);
      hang.subtotals = calcSubtotals(allHangRows);
      hang.showSubtotal = hang.mokGroups.length >= 2;
      hang.totalRows = hang.mokGroups.length * 3 + (hang.showSubtotal ? 3 : 0);
    }
    const allGwanRows = gwan.hangGroups.flatMap((h: any) =>
      h.mokGroups.flatMap((m: any) => m.rows)
    );
    gwan.subtotals = calcSubtotals(allGwanRows);
    gwan.showSubtotal = gwan.hangGroups.length >= 2;
    gwan.totalRows = gwan.hangGroups.reduce((sum: number, h: any) => sum + h.totalRows, 0)
      + (gwan.showSubtotal ? 3 : 0);
  }
  return gwanGroups;
};

const groupedIn = computed(() => groupData(payload.value?.income?.rows || []));
const groupedOut = computed(() => groupData(payload.value?.expense?.rows || []));
const inGubunRows = computed(() =>
  groupedIn.value.reduce((sum: number, g: any) => sum + g.totalRows, 0)
);
const outGubunRows = computed(() =>
  groupedOut.value.reduce((sum: number, g: any) => sum + g.totalRows, 0)
);
const inTotals = computed(() => payload.value?.income?.totals || emptyTotals());
const outTotals = computed(() => payload.value?.expense?.totals || emptyTotals());

const formatAmt = (n: number | null | undefined) => (Number(n) || 0).toLocaleString('ko-KR');
const sourceClass = (source: string) => ({
  'source-budget': source === '예산',
  'source-closing': source === '결산',
  'source-diff': source === '증감',
});
const negClass = (row: { source?: string; 총액?: number } | AmtRow) => ({
  negative: parseAmt((row as any).총액) < 0,
});

const load = async () => {
  if (!props.memberCode || !props.year || !props.month) return;
  loading.value = true;
  error.value = '';
  payload.value = null;
  try {
    const res = await bankbookApi.getSettlementView(String(props.memberCode), {
      year: String(props.year),
      month: String(props.month),
      bizGubun: props.bizGubun || undefined,
      bankCode: props.bankCode,
    });
    payload.value = res.data?.data || null;
    if (!payload.value) error.value = '결산 데이터가 없습니다.';
  } catch (e: any) {
    console.error(e);
    error.value = e?.response?.data?.message || '결산을 불러오지 못했습니다.';
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      mainGubun.value = '전체';
      load();
    }
  }
);
</script>
